import {
    AuthenticatedTemplate,
    IMsalContext,
    MsalAuthenticationTemplate,
    UnauthenticatedTemplate,
    useMsal,
} from "@azure/msal-react";
import { InteractionType } from "@azure/msal-browser";

import AuthService from "@/services/auth-service";
import { AuthInteractionType } from "@/config/auth";
import { Outlet, useNavigate } from "react-router";
import { Spinner } from "./components/ui/spinner";
import ErrorUI from "./components/ui/error-ui";
import { Button } from "./components/ui/button";
import { useUser } from "./contexts/user";
import NoAuthDeprecated from "./components/no-auth/no-auth-deprecated";
import NoAuth from "./components/no-auth/no-auth";
import { PropsWithChildren } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { FeatureFlag } from "./constants";

const debug: boolean = false;

/**
 * Route guard component properties.
 */
type AuthRouteGuardProps = {
    /**
     * Authentication interaction type for the route (popup/redirect/silent).
     * The default is redirect.
     */
    interactionType?: InteractionType;
};

const MsalErrorComponent = () => {
    const navigate = useNavigate();
    const { instance: msalInstance } = useMsal();

    const handleGoToLoginPage = () => {
        msalInstance.clearCache();
        navigate("/login");
    };

    return (
        <ErrorUI
            type="cover"
            description="We ran into an issue and couldn’t authorise you. Please log in again."
            actionButtons={[
                <Button key={"navigate"} onClick={handleGoToLoginPage}>
                    Go to log in page
                </Button>,
            ]}
        />
    );
};

const MsalLoadingComponent = (props?: IMsalContext) => {
    if (debug) {
        console.debug(
            "MSAL loading status change:",
            props?.inProgress.toString(),
        );
    }

    return <Spinner sizing="largest" />;
};

export const AuthRouteGuard = ({
    interactionType = AuthInteractionType,
}: AuthRouteGuardProps) => {
    const authenticationRequest = AuthService.createAuthRequest({
        interactionType: interactionType,
    });

    return (
        <MsalAuthenticationTemplate
            interactionType={interactionType}
            authenticationRequest={authenticationRequest}
            errorComponent={MsalErrorComponent}
            loadingComponent={MsalLoadingComponent}
        >
            <UserCheck>
                <Outlet />
            </UserCheck>
        </MsalAuthenticationTemplate>
    );
};

const UserCheck = ({ children }: PropsWithChildren) => {
    const { user, isLoading } = useUser();
    const flags = useFlags();
    const queryParams = new URLSearchParams(window.location.search);
    const logoutMessage = queryParams.get("logout");

    if (isLoading) {
        return <Spinner sizing="largest" cover />;
    } else if (user && user.userType !== "EnterpriseUser") {
        // Logging in as EndUser is not currently allowed at API level, placeholder logic only
        return flags[FeatureFlag.IL_4194_POWER_MOVE] ? (
            <NoAuth
                message="You do not have permission to access Illuminate"
                hideLogInOut
                redirectIfAuthenticated={false}
            />
        ) : (
            <NoAuthDeprecated
                message="You do not have permission to access Illuminate"
                hideLogInOut
                redirectIfAuthenticated={false}
            />
        );
    } else if (user && (!user.organisationId || !user.permissions?.length)) {
        return flags[FeatureFlag.IL_4194_POWER_MOVE] ? (
            <NoAuth
                message="Thanks for joining Illuminate. Your organisation will be confirmed and we'll notify you once your access has been approved."
                redirectIfAuthenticated={false}
            />
        ) : (
            <NoAuthDeprecated
                message="Thanks for joining Illuminate. Your organisation will be confirmed and we'll notify you once your access has been approved."
                redirectIfAuthenticated={false}
            />
        );
    } else {
        return (
            <>
                <UnauthenticatedTemplate>
                    {flags[FeatureFlag.IL_4194_POWER_MOVE] ? (
                        <NoAuth
                            title1={"Welcome to Illuminate"}
                            title2={
                                logoutMessage === "success"
                                    ? "You've been logged out. Log back in to manage your charging network. "
                                    : "You are currently not logged in."
                            }
                            desiredDirectory={"adb2c"}
                        />
                    ) : (
                        <NoAuthDeprecated
                            title1={"WELCOME TO"}
                            title2={"Illuminate"}
                            title3={"Charging platform of the future."}
                            message={
                                logoutMessage === "success"
                                    ? "Logout successful! You have been logged out. "
                                    : "You are currently not logged in. "
                            }
                            messageSpan={
                                logoutMessage === "success"
                                    ? "Please click the login button to login."
                                    : "Please log in to continue"
                            }
                            desiredDirectory={"adb2c"}
                        />
                    )}
                </UnauthenticatedTemplate>
                <AuthenticatedTemplate>{children}</AuthenticatedTemplate>
            </>
        );
    }
};
